import React, { useEffect, useState } from "react";
import { formatDate } from "@/lib/formatDate";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Boxes, Plus, Pencil, Trash2, ArrowDownUp, AlertTriangle } from "lucide-react";
import { EmptyState } from "@/components/EmptyState";

const EMPTY_FORM = {
  sku: "", name: "", description: "", unit: "Stk",
  sale_price: "", purchase_price: "", vat_rate: "8.1",
  income_account_id: "3200", stock_managed: false, initial_stock: "", min_stock: "",
};

export function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [stockProduct, setStockProduct] = useState(null);
  const [movements, setMovements] = useState([]);
  const [stockForm, setStockForm] = useState({ type: "in", quantity: "", note: "" });
  const [stockSaving, setStockSaving] = useState(false);

  const companyId = localStorage.getItem("demoCompanyId");

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await apiClient.get(`/companies/${companyId}/products`, { params: { include_inactive: true } });
      setProducts(res.data || []);
    } catch (e) {
      console.error(e);
      toast.error("Fehler beim Laden der Produkte");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowDialog(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      sku: p.sku || "", name: p.name || "", description: p.description || "", unit: p.unit || "Stk",
      sale_price: String(p.sale_price ?? ""), purchase_price: String(p.purchase_price ?? ""),
      vat_rate: String(p.vat_rate ?? "8.1"), income_account_id: p.income_account_id || "3200",
      stock_managed: !!p.stock_managed, initial_stock: "", min_stock: String(p.min_stock ?? ""),
    });
    setShowDialog(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name ist Pflicht."); return; }
    setSaving(true);
    try {
      const payload = {
        sku: form.sku.trim(),
        name: form.name.trim(),
        description: form.description || null,
        unit: form.unit || "Stk",
        sale_price: parseFloat(form.sale_price) || 0,
        purchase_price: parseFloat(form.purchase_price) || 0,
        vat_rate: parseFloat(form.vat_rate) || 0,
        income_account_id: form.income_account_id || "3200",
        stock_managed: !!form.stock_managed,
        min_stock: parseFloat(form.min_stock) || 0,
      };
      if (editing) {
        await apiClient.put(`/products/${editing._id}`, payload);
        toast.success("Artikel aktualisiert");
      } else {
        await apiClient.post(`/companies/${companyId}/products`, {
          ...payload,
          initial_stock: parseFloat(form.initial_stock) || 0,
        });
        toast.success("Artikel angelegt");
      }
      setShowDialog(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Artikel "${p.name}" wirklich löschen?`)) return;
    try {
      await apiClient.delete(`/products/${p._id}`);
      toast.success("Artikel gelöscht");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const openStock = async (p) => {
    setStockProduct(p);
    setStockForm({ type: "in", quantity: "", note: "" });
    try {
      const res = await apiClient.get(`/products/${p._id}/stock-movements`);
      setMovements(res.data || []);
    } catch { setMovements([]); }
  };

  const handleStockSave = async (e) => {
    e.preventDefault();
    const qty = parseFloat(stockForm.quantity);
    if (!qty || qty <= 0) { toast.error("Menge muss > 0 sein."); return; }
    setStockSaving(true);
    try {
      const res = await apiClient.post(`/products/${stockProduct._id}/stock-movements`, {
        type: stockForm.type,
        quantity: qty,
        note: stockForm.note || null,
      });
      toast.success(`Neuer Bestand: ${res.data.stock_qty} ${res.data.unit}`);
      setStockProduct(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lagerbewegung fehlgeschlagen");
    } finally {
      setStockSaving(false);
    }
  };

  const fmt = (n) => Number(n || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Produkte & Lager</h1>
          <p className="mt-1 text-sm text-slate-500">
            Artikelstamm mit Preisen, MWST und optionaler Lagerverwaltung mit Mindestbestands-Warnung.
          </p>
        </div>
        <Button size="sm" className="rounded-full bg-slate-900 text-slate-50" onClick={openCreate} data-testid="products-new-button">
          <Plus className="h-3.5 w-3.5 mr-1.5" /> Neuer Artikel
        </Button>
      </div>

      <Card className="p-4 border border-slate-200 bg-white shadow-sm" data-testid="products-list-card">
        {loading && <div className="text-xs text-slate-400 mb-2">Lade …</div>}
        <Table data-testid="products-table">
          <TableHeader>
            <TableRow>
              <TableHead>Art.-Nr.</TableHead>
              <TableHead>Bezeichnung</TableHead>
              <TableHead>Einheit</TableHead>
              <TableHead className="text-right">VK-Preis</TableHead>
              <TableHead className="text-right">EK-Preis</TableHead>
              <TableHead className="text-right">MWST</TableHead>
              <TableHead className="text-right">Lager</TableHead>
              <TableHead className="text-right">Aktionen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="p-0">
                  <EmptyState
                    testid="products-empty-state"
                    icon={<Boxes className="h-5 w-5" />}
                    title="Noch keine Artikel"
                    description="Legen Sie Produkte und Dienstleistungen an — mit Preisen und optionaler Lagerverwaltung."
                  />
                </TableCell>
              </TableRow>
            )}
            {products.map((p) => (
              <TableRow key={p._id} data-testid={`products-row-${p._id}`} className={p.active === false ? "opacity-50" : ""}>
                <TableCell className="font-mono text-xs">{p.sku || "—"}</TableCell>
                <TableCell>
                  <div className="font-medium text-slate-900">{p.name}</div>
                  {p.description && <div className="text-[11px] text-slate-500 truncate max-w-[280px]">{p.description}</div>}
                </TableCell>
                <TableCell>{p.unit}</TableCell>
                <TableCell className="text-right tabular-nums">{fmt(p.sale_price)}</TableCell>
                <TableCell className="text-right tabular-nums">{fmt(p.purchase_price)}</TableCell>
                <TableCell className="text-right">{p.vat_rate}%</TableCell>
                <TableCell className="text-right">
                  {p.stock_managed ? (
                    <span className="inline-flex items-center gap-1.5">
                      {p.low_stock && (
                        <Badge className="bg-amber-100 text-amber-900 border-amber-200 text-[10px] h-5 border" data-testid={`products-low-stock-${p._id}`}>
                          <AlertTriangle className="h-3 w-3 mr-0.5" /> Min!
                        </Badge>
                      )}
                      <span className="tabular-nums font-medium">{p.stock_qty} {p.unit}</span>
                    </span>
                  ) : (
                    <span className="text-slate-400 text-xs">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right space-x-1">
                  {p.stock_managed && (
                    <Button variant="outline" size="sm" onClick={() => openStock(p)} title="Lagerbewegung" data-testid={`products-stock-button-${p._id}`}>
                      <ArrowDownUp className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => openEdit(p)} title="Bearbeiten" data-testid={`products-edit-button-${p._id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="outline" size="sm" className="border-rose-200 text-rose-700 hover:bg-rose-50" onClick={() => handleDelete(p)} title="Löschen" data-testid={`products-delete-button-${p._id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={(o) => !o && setShowDialog(false)}>
        <DialogContent className="max-w-lg" data-testid="products-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Artikel bearbeiten" : "Neuer Artikel"}</DialogTitle>
          </DialogHeader>
          <form className="space-y-3" onSubmit={handleSave}>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>Art.-Nr. (SKU)</Label>
                <Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} placeholder="A-1001" data-testid="products-sku-input" />
              </div>
              <div className="col-span-2 space-y-1">
                <Label>Bezeichnung *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="products-name-input" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Beschreibung</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="products-description-input" />
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div className="space-y-1">
                <Label>Einheit</Label>
                <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} data-testid="products-unit-input" />
              </div>
              <div className="space-y-1">
                <Label>VK-Preis</Label>
                <Input type="number" step="0.01" value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: e.target.value })} data-testid="products-sale-price-input" />
              </div>
              <div className="space-y-1">
                <Label>EK-Preis</Label>
                <Input type="number" step="0.01" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} data-testid="products-purchase-price-input" />
              </div>
              <div className="space-y-1">
                <Label>MWST %</Label>
                <Input type="number" step="0.1" value={form.vat_rate} onChange={(e) => setForm({ ...form, vat_rate: e.target.value })} data-testid="products-vat-input" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Ertragskonto</Label>
              <Input value={form.income_account_id} onChange={(e) => setForm({ ...form, income_account_id: e.target.value })} placeholder="3200" data-testid="products-income-account-input" />
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-3">
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.stock_managed} onChange={(e) => setForm({ ...form, stock_managed: e.target.checked })} data-testid="products-stock-managed-checkbox" />
                <span className="text-slate-700">Lagerverwaltung aktivieren</span>
              </label>
              {form.stock_managed && (
                <div className="grid grid-cols-2 gap-3">
                  {!editing && (
                    <div className="space-y-1">
                      <Label>Anfangsbestand</Label>
                      <Input type="number" step="0.001" value={form.initial_stock} onChange={(e) => setForm({ ...form, initial_stock: e.target.value })} data-testid="products-initial-stock-input" />
                    </div>
                  )}
                  <div className="space-y-1">
                    <Label>Mindestbestand</Label>
                    <Input type="number" step="0.001" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} data-testid="products-min-stock-input" />
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)} data-testid="products-form-cancel">Abbrechen</Button>
              <Button type="submit" disabled={saving} data-testid="products-form-submit">
                {saving ? "Speichert …" : editing ? "Speichern" : "Anlegen"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Stock Movement Dialog */}
      <Dialog open={!!stockProduct} onOpenChange={(o) => !o && setStockProduct(null)}>
        <DialogContent className="max-w-md" data-testid="products-stock-dialog">
          <DialogHeader>
            <DialogTitle>Lagerbewegung — {stockProduct?.name}</DialogTitle>
            <DialogDescription>
              Aktueller Bestand: <span className="font-mono font-medium">{stockProduct?.stock_qty} {stockProduct?.unit}</span>
            </DialogDescription>
          </DialogHeader>
          <form className="space-y-3" onSubmit={handleStockSave}>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Typ</Label>
                <Select value={stockForm.type} onValueChange={(v) => setStockForm({ ...stockForm, type: v })}>
                  <SelectTrigger data-testid="products-stock-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in">Zugang (+)</SelectItem>
                    <SelectItem value="out">Abgang (−)</SelectItem>
                    <SelectItem value="adjust">Inventur (Bestand setzen)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Menge</Label>
                <Input type="number" step="0.001" min="0" value={stockForm.quantity} onChange={(e) => setStockForm({ ...stockForm, quantity: e.target.value })} data-testid="products-stock-qty-input" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Notiz (optional)</Label>
              <Input value={stockForm.note} onChange={(e) => setStockForm({ ...stockForm, note: e.target.value })} placeholder="z.B. Lieferung Nr. 4711" data-testid="products-stock-note-input" />
            </div>
            {movements.length > 0 && (
              <div className="max-h-40 overflow-y-auto rounded border border-slate-200 divide-y divide-slate-100 text-xs" data-testid="products-stock-history">
                {movements.map((m) => (
                  <div key={m._id} className="flex items-center justify-between px-2 py-1.5">
                    <span className="text-slate-500">{formatDate(m.date)}</span>
                    <span className={m.type === "out" ? "text-rose-700" : "text-emerald-700"}>
                      {m.type === "in" ? "+" : m.type === "out" ? "−" : "="} {m.quantity}
                    </span>
                    <span className="text-slate-400 truncate max-w-[140px]">{m.note || ""}</span>
                  </div>
                ))}
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setStockProduct(null)} data-testid="products-stock-cancel">Abbrechen</Button>
              <Button type="submit" disabled={stockSaving} data-testid="products-stock-submit">
                {stockSaving ? "Bucht …" : "Buchen"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
